import coalService from './coal-service.js';

/**
 * Diz se o servidor recusou a foto (422: imagem inválida ou grande demais; 404: ficha inexistente).
 * Repetir o envio não resolve, a foto precisa ser removida.
 */
export function isRejectedImage(image) {
    return image.status === 'error' && (image.errorStatus === 422 || image.errorStatus === 404);
}

/**
 * Aplica no servidor o que mudou nas fotos de uma ficha já salva, uma foto por requisição.
 *
 * - items: lista atual da tela; cada item é { key, id?, photo, status }, com status
 *   'saved' (já no servidor), 'pending' (escolhida agora) ou 'error' (falhou antes).
 * - removedImages: itens que já estavam salvos e que o usuário removeu (têm `id`).
 * - onProgress(atual, total): chamado antes de enviar cada foto nova.
 *
 * Primeiro apaga as removidas, depois envia as novas em fila (uma de cada vez). Foto recusada
 * pelo servidor (ver isRejectedImage) não é reenviada: continua na lista até o usuário removê-la.
 *
 * Devolve:
 * - items: a lista nova para a tela. Foto nova que falhou fica com status 'error' e `errorStatus`
 *   (status HTTP, ou undefined em falha de rede/timeout);
 * - failedRemovals: remoções que falharam. Ficam FORA de `items` e o chamador deve devolvê-las à
 *   fila de remoção, para o próximo "Salvar" repetir a remoção (a foto continua no servidor);
 * - failed: quantas operações falharam (envios + remoções), incluindo as fotos recusadas;
 * - rejected: quantas fotos o servidor recusou, que só se resolvem removendo a foto.
 */
export async function syncCoalImages(coalId, items, removedImages, onProgress = () => {}) {
    const failedRemovals = [];

    for (const item of removedImages) {
        try {
            await coalService.deleteCoalImage(coalId, item.id);
        } catch (err) {
            // 404: a foto já não está nessa ficha, então para o usuário ela já foi removida
            if (err?.response?.status !== 404) failedRemovals.push(item);
        }
    }

    const total = items.filter(i => i.status !== 'saved' && !isRejectedImage(i)).length;
    let sent = 0;
    const result = [];

    for (const item of items) {
        // já salva, ou recusada pelo servidor: não reenvia (a recusada só sai se o usuário removê-la)
        if (item.status === 'saved' || isRejectedImage(item)) {
            result.push(item);
            continue;
        }

        onProgress(++sent, total);
        try {
            const { data } = await coalService.addCoalImage(coalId, item.photo);
            // monta o item do zero para não carregar o errorStatus de uma tentativa anterior
            result.push({ key: 'img-' + data.id, id: data.id, photo: item.photo, status: 'saved' });
        } catch (err) {
            result.push({ ...item, status: 'error', errorStatus: err?.response?.status });
        }
    }

    const failed = result.filter(i => i.status === 'error').length + failedRemovals.length;
    const rejected = result.filter(isRejectedImage).length;
    return { items: result, failedRemovals, failed, rejected };
}
